/**
 * Per-route SEO meta. Renders a Helmet block with title, description,
 * canonical URL, and the OG/Twitter title+description+url overrides for
 * a single static marketing route.
 *
 * Why this exists
 * ---------------
 * src/App.tsx's `DefaultSocialMeta` provides the homepage default so
 * every route has *something*, but if every page inherits the same
 * <title> and meta description, Google and social crawlers see the
 * homepage tagline on /about, /contact, etc., which is bad for ranking
 * and for unfurls. Helmet uses "deepest-wins" dedup, so a per-route
 * <PageMeta /> cleanly overrides the defaults for that route only.
 *
 * og:image is intentionally not overridden here, the default banner
 * from DefaultSocialMeta is correct for every static marketing route.
 * If a route ever wants its own banner, add an `image` prop.
 */
import { Helmet } from "@dr.pogodin/react-helmet";

const SITE_URL = "https://lembra.com.au";

interface PageMetaProps {
  /** Page <title>. Should be unique per route and include "Lembra". */
  title: string;
  /** Meta description. Aim for 140-160 chars; this is what Google shows
   *  in the search snippet. */
  description: string;
  /** Path with leading slash, e.g. "/about". Used to build the canonical
   *  URL and og:url. Pass "/" for the homepage. */
  path: string;
  /**
   * MS189: when true, emit `<meta name="robots" content="noindex,follow">`
   * so well-behaved crawlers skip this page. Use for token-bearing URLs
   * (/gift/:token, future /share/:token, etc.) where the URL itself is
   * the access-control mechanism and indexing it leaks the contents.
   *
   * `follow` (not `nofollow`) is intentional — we don't want THIS
   * surface indexed but the recovery links on it (Home, Browse, etc.)
   * are still worth crawling. robots.txt is the first line of defence;
   * this meta is the belt-and-braces.
   */
  noindex?: boolean;
}

export const PageMeta = ({ title, description, path, noindex }: PageMetaProps) => {
  const url = path === "/" ? SITE_URL : `${SITE_URL}${path}`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {noindex && <meta name="robots" content="noindex,follow" />}
    </Helmet>
  );
};
